import Tour from '@core/components/Tour';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { steps } from './components/steps/steps';
import { confirmarAlert, excluirAlert } from 'app/services/alerts';
import { contaApi } from 'app/services/api/contaApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import transferenciaApi from 'app/services/api/transferenciaApi';
import * as Actions from 'app/store/actions';
import moment from 'moment';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { TransferenciaCadastroModal } from './components/modal/cadastro';
import { TransferenciaModais } from './components/modal/TransferenciaModais';
import TransferenciaSearchHeader from './components/TransferenciaSearchHeader';
import TransferenciaTable from './components/TransferenciaTable';
import { TransferenciaContext, TransferenciaProvider } from './TransferenciaContext';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import { GlobalContext } from 'app/store/globalcontext';
import { registroTransferencia } from '@core/util/mockTourUtils';

const TRANSFERENCIA = 'Transferência';

const Transferencia = () => {
  const [, setTransferencia] = useContext(TransferenciaContext).transferencia;
  const [, setSelecao] = useContext(TransferenciaContext).selecao;
  const [loading, setLoading] = useContext(TransferenciaContext).loading;
  const [, setTransferencias] = useContext(TransferenciaContext).transferencias;
  const [, setSearch] = useContext(TransferenciaContext).search;
  const [, setTotalElements] = useContext(TransferenciaContext).totalElements;
  const [, setModal] = useContext(TransferenciaContext).modal;
  const [, setPage] = useContext(TransferenciaContext).page;
  const [, setSize] = useContext(TransferenciaContext).size;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;

  const history = useHistory();

  const [run, setRun] = useState(false);
  const [tour, setTour] = useState(false);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  useReduxLoading(loading);

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const resetSelecao = () => {
    setSelecao([]);
  };

  const initialPage = {
    page: 0,
    query: '',
    size: 15,
    sort: '',
  };

  const buscarTour = async () => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'TRANSFERENCIAS');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setTour(true);
      setTotalElements(registroTransferencia.totalElements);
      setPage(registroTransferencia.page);
      setSize(registroTransferencia.size);
      setTransferencias(registroTransferencia.content);
    } else {
      fetchTransferencias();
    }
  };

  const handleSubmit = async (params) => {
    setLoading(true);
    let message;
    let response;

    if (params.origemId === params.destinoId) {
      message = 'Não é permitido realizar transferência para a mesma conta.';
      showError(message);
      setLoading(false);
    } else {
      try {
        const values = JSON.stringify(params);
        if (params.id) {
          eventReactGA(TRANSFERENCIA, 'Atualizar transferência');
          response = await transferenciaApi().atualizar(values);
          message = 'Transferência editada com sucesso.';
        } else {
          eventReactGA(TRANSFERENCIA, 'Cadastrar transferência');
          response = await transferenciaApi().cadastrar(values);
          message = 'Transferência salva com sucesso.';
        }

        setModal('');
        fetchTransferencias(initialPage);
        showSuccess(message);
      } catch (err) {
        apiUtils.handleApiError(err);
      } finally {
        setLoading(false);
      }
    }

    return response;
  };

  const handleOpenModalCadastro = async () => {
    setModal(TransferenciaModais.cadastrarTransferencia);
  };

  const desconciliar = async (id) => {
    try {
      const respDesconciliacao = await transferenciaApi().desconciliar(id);
      if (respDesconciliacao.status === 200) {
        showSuccess('Desconciliação realizada com sucesso.');
        fetchTransferencias();
      }
    } catch (error) {
      apiUtils.handleApiError(error);
      setLoading(false);
    }
    setLoading(false);
  };

  const desconciliarEAbrirModalEdicao = async (id) => {
    setLoading(true);
    try {
      await desconciliar(id);
      await buscarTransferencia(id);
      handleOpenModalCadastro();
    } catch (error) {
      apiUtils.handleApiError(error);
      setLoading(false);
    }
    setLoading(false);
  };

  const handleOpenModalEditar = async (transferencia) => {
    setLoading(true);

    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('TRANSFERENCIAS', transferencia.data);
    if (response.data) {
      if (response.data.bloquearAnteriores) {
        if (
          moment(transferencia.data).isSameOrBefore(response.data.periodo) &&
          response.data.funcionalidades.includes('TRANSFERENCIAS')
        ) {
          setLoading(false);
          throw showError(
            'Não é possível lançar, editar e excluir transferências em data com período já fechado no sistema.'
          );
        }
      }
    }

    try {
      if (transferencia.conciliado) {
        confirmarAlert({
          confirmar: () => desconciliarEAbrirModalEdicao(transferencia.id),
          titulo: 'Não é possível realizar a edição pois a transferência bancária está conciliada.',
          mensagem: 'Deseja desconciliar para realizar a edição?',
        });
      } else {
        await buscarTransferencia(transferencia.id);
        handleOpenModalCadastro();
      }
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  async function buscarTransferencia(id) {
    try {
      setLoading(true);
      const resp = await transferenciaApi().buscar(id);
      const origem = await contaApi.getOne(resp?.data?.origem?.id);
      const destino = await contaApi.getOne(resp?.data?.destino?.id);

      const transferencia = {
        id: resp?.data?.id,
        contaOrigemId: origem.data,
        contaDestinoId: destino.data,
        valor: resp?.data?.valor,
        data: resp?.data?.data,
        descricao: resp?.data?.descricao,
        conciliado: resp?.data?.conciliado,
        edicao: true,
      };

      setTransferencia(transferencia);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  }

  const excluir = async (transferencias, pageable) => {
    try {
      const params = transferencias.ids.map((transferencia) => transferencia.id);

      eventReactGA(TRANSFERENCIA, 'Excluir transferência');
      await transferenciaApi().excluir(params);
      showSuccess(
        params.length === 1 ? 'Transferência excluída com sucesso.' : 'Transferências excluídas com sucesso.'
      );
      resetSelecao();
      fetchTransferencias(pageable);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
  };

  const handleDelete = async (transferencias, pageable) => {
    try {
      const conciliadas = transferencias.ids.filter((transferencia) => transferencia.conciliado);
      if (conciliadas.length > 0) {
        confirmarAlert({
          confirmar: async () => excluir(transferencias, pageable),
          titulo: 'Não é possível realizar a exclusão pois a transferência bancária está conciliada.',
          mensagem: 'Deseja desconciliar para realizar a exclusão?',
        });
      } else {
        excluirAlert({
          opcoes: {
            title: 'Você realmente deseja excluir?',
            text: '',
            confirmButtonText: 'Sim',
            cancelButtonText: 'Não',
          },
          confirmar: async () => excluir(transferencias, pageable),
        });
      }
    } catch (err) {
      apiUtils.handleApiError(err);
    }
  };

  const fetchTransferencias = async (search = initialPage) => {
    setLoading(true);
    try {
      setSearch(search);
      const response = await transferenciaApi().buscarTodas(search);
      setTotalElements(response.data.totalElements);
      setPage(response.data.page);
      setSize(response.data.size);
      setTransferencias(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const onInit = () => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    buscarTour();
  };

  useEffect(onInit, []);

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('TRANSFERENCIAS');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchTransferencias();
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tour steps={steps} run={run} setRun={setRun} onFinish={() => atualizarTour()} disableScrolling={true} />
      <FusePageCarded
        innerScroll
        header={<TransferenciaSearchHeader fetch={fetchTransferencias} initialPage={initialPage} />}
        content={
          <>
            <TransferenciaTable
              fetchData={fetchTransferencias}
              onSelecaoChange={setSelecao}
              cadastrar={handleOpenModalCadastro}
              editar={handleOpenModalEditar}
              onExcluir={handleDelete}
              onTour={tour}
            />
            <TransferenciaCadastroModal onSubmit={handleSubmit} />
          </>
        }
      />
    </>
  );
};

const TransferenciaContainer = () => (
  <TransferenciaProvider>
    <Transferencia />
  </TransferenciaProvider>
);

export default TransferenciaContainer;
